import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS, Checkout } from "@lib/constants/contentful";
import { type FC, type Dispatch, type SetStateAction, type ReactNode, useMemo } from "react";
import type { AccordionItemProps } from "@ecco/ui/accordion";
import { AccordionButton, AccordionItem, AccordionPanel } from "@ecco/ui/accordion";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Surface } from "@ecco/ui/surface";
import { SurfaceSection } from "@ecco/ui/surface-section";
import { H } from "@ecco/ui/a11y-heading";
import { Button } from "@ecco/ui/button";
import { opacities } from "@ecco/ui/tokens";
import type { ErrorMessageType } from "@features/checkout/utils/useCheckoutErrorState";
import { CheckoutStep } from "@features/checkout/types";

export interface StepFormProps extends AccordionItemProps {
    stepId: CheckoutStep;
    title: string;
    isExpandedId: CheckoutStep;
    errorMessage?: ErrorMessageType;
    onSubmit?: (...args: any[]) => void;
    handleExpanded: Dispatch<SetStateAction<CheckoutStep>>;
    values?: Readonly<any>;
    children?: ReactNode;
    accordionHeadingContent?: ReactNode;
    summary?: ReactNode;
}

const StepForm: FC<StepFormProps> = ({
    title,
    stepId,
    isExpandedId,
    handleExpanded,
    values,
    children,
    errorMessage,
    accordionHeadingContent,
    summary,
    ...otherProps
}) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.page, [
        CommonCMS.edit,
        Checkout.step1.formEmail.title,
        Checkout.step1.formPhone.title,
        Checkout.step2.billingAddress,
        Checkout.step2.deliveryMethod,
        Checkout.step2.shippingAddress,
    ]);
    const headers = {
        edit: microcopies[CommonCMS.edit],
        email: microcopies[Checkout.step1.formEmail.title],
        phone: microcopies[Checkout.step1.formPhone.title],
        billingAddress: microcopies[Checkout.step2.billingAddress],
        deliveryMethod: microcopies[Checkout.step2.deliveryMethod],
        shippingAddress: microcopies[Checkout.step2.shippingAddress],
    };

    const setExpanded = () => handleExpanded(stepId);
    const isExpanded = isExpandedId === stepId;
    const isPastStep = stepId < isExpandedId;
    const showSummary = typeof summary !== "undefined" && isPastStep;
    const isOpenBlock = isExpanded || values?.initialized;

    const shouldShowEditButton = useMemo(() => {
        // Don't show while expanded
        if (isExpanded) return false;
        // Don't show if checkout is not fully initialized
        if (!values?.initialized) return false;
        // Don't show on Payment step
        if (stepId === CheckoutStep.PAYMENT) return false;
        // By default show whenever the step is passed
        return isPastStep;
    }, [stepId, values, isPastStep, isExpanded]);

    return (
        <AccordionItem
            transition={"background-color 200ms linear"}
            backgroundColor={isOpenBlock ? "white" : `rgba(255,255,255,${opacities[50]})`}
            {...otherProps}
            mb="0.2"
            pb={{
                base: 0,
                md: isPastStep ? 0 : 6,
            }}
        >
            <Box
                px={[3, 6, 6]}
                paddingTop={[6, 6, 6]}
                paddingBottom={isExpanded ? 0 : [6, 6, isOpenBlock ? 6 : 0]}
            >
                <Flex direction="column">
                    <Flex justify="space-between" flexDirection="row" alignItems="center">
                        <H
                            level="h2"
                            as="h2"
                            size={"h5"}
                            opacity={isOpenBlock ? 1 : 0.6}
                            textTransform="uppercase"
                            mb={4}
                        >
                            {title}
                        </H>
                        <AccordionButton
                            display="block"
                            visibility={shouldShowEditButton ? "visible" : "hidden"}
                            onClick={setExpanded}
                            width="fit-content"
                            p={0}
                            _hover={{
                                bg: "inherit",
                                color: "black",
                                textDecorationColor: "black",
                            }}
                            minW="unset"
                            color="gray.500"
                            as={Button}
                            variant={"link"}
                        >
                            {headers.edit}
                        </AccordionButton>
                    </Flex>
                    {accordionHeadingContent && (
                        <Box mb={4} display="flex" justifyContent="flex-start">
                            {accordionHeadingContent}
                        </Box>
                    )}
                    {errorMessage && (
                        <Surface data-checkout-error colorScheme="red" size="sm" mb={4}>
                            <SurfaceSection>{errorMessage}</SurfaceSection>
                        </Surface>
                    )}

                    {showSummary && summary}
                </Flex>
            </Box>

            {isExpanded && (
                <AccordionPanel px={[3, 6, 6]} pb={[4, 6, 6]}>
                    {children}
                </AccordionPanel>
            )}
        </AccordionItem>
    );
};

export default StepForm;
